import { TFunction } from "i18next";
import { AlertDetails, AlertReason } from "../models/entities/alert.model";
import { TaskRow } from "../models/pages/task-row.model";

// ===== Sustained out-of-range (SOOR) alert grounding metadata (KAN-1859) =====
// The alert carries a first-class `task.metadata` JSONB field, delivered as a
// JSON string on the wire (same contract as `resolution_criteria`). Shape:
//   { "outOfRange": [{ "measurement", "average", "threshold", "direction" }, ...] }
// See karchitecture/planning/kan-declarative-alerts-migration.md §3.2.

export type OutOfRangeMeasurement = "SYSTOLIC" | "DIASTOLIC" | "FASTING" | "PP" | "UNLABELED";

export interface OutOfRangeEntry {
	measurement: string;
	average: number;
	threshold?: number;
	direction?: string;
}

// Human-readable labels, hardcoded to match the weight-critical sub-line
// convention (which likewise hardcodes "Weight:" / "Timeframe:"). Unknown
// measurements fall back to the raw code so a row never renders blank.
export const OUT_OF_RANGE_MEASUREMENT_LABELS: Record<OutOfRangeMeasurement, string> = {
	SYSTOLIC: "Systolic",
	DIASTOLIC: "Diastolic",
	FASTING: "Fasting",
	PP: "Post-prandial",
	UNLABELED: "Unlabeled",
};

export const outOfRangeMeasurementLabel = (measurement: string): string =>
	OUT_OF_RANGE_MEASUREMENT_LABELS[measurement as OutOfRangeMeasurement] ?? measurement;

/** The one label string for a SOOR average, e.g. "Systolic — Avg (152)". Shared by the Requires attention Details cell and the Home worklist row (KAN-2641) so the two can never drift. */
export const formatOutOfRangeAverage = (entry: OutOfRangeEntry): string =>
	`${outOfRangeMeasurementLabel(entry.measurement)} — Avg (${entry.average})`;

/**
 * Safely parse the alert `task.metadata` JSON string into its out-of-range
 * entries. The field may be null, undefined, malformed JSON, or shaped
 * unexpectedly (legacy rows have no metadata at all) — in every such case this
 * returns null so callers fall back to the plain `details` label and never
 * crash a row. Entries are returned in the order the backend emits them
 * (canonical systolic→diastolic / fasting→pp→unlabeled per the contract).
 */
export const parseAlertMetadata = (
	metadata: string | null | undefined
): OutOfRangeEntry[] | null => {
	if (!metadata) {
		return null;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(metadata);
	} catch {
		return null;
	}

	const outOfRange = (parsed as { outOfRange?: unknown } | null)?.outOfRange;
	if (!Array.isArray(outOfRange)) {
		return null;
	}

	const entries = outOfRange.filter(
		(entry): entry is OutOfRangeEntry =>
			!!entry &&
			typeof entry === "object" &&
			typeof (entry as OutOfRangeEntry).measurement === "string" &&
			typeof (entry as OutOfRangeEntry).average === "number"
	);

	return entries.length > 0 ? entries : null;
};

export interface AbnormalWeightGain {
	prefix: string;
	weight: number | null;
	timeframe: string | null;
}

export const parseAbnormalWeightGain = (details: string): AbnormalWeightGain | null => {
	const abnormalWeightGainPrefix = "ABNORMAL_WEIGHT_GAIN";

	if (!details?.startsWith(abnormalWeightGainPrefix)) {
		return null;
	}

	const remaining = details.replace(abnormalWeightGainPrefix, "");

	if (remaining === "") {
		return { prefix: abnormalWeightGainPrefix, weight: null, timeframe: null };
	}

	const regex = /^_(\d+)_(.+)$/;
	const match = regex.exec(remaining);

	if (match) {
		const [, weight, timeframePart] = match;
		return {
			prefix: abnormalWeightGainPrefix,
			weight: parseInt(weight, 10) / 10,
			timeframe: timeframePart.toLowerCase().replace(/_/g, " "),
		};
	}

	return { prefix: abnormalWeightGainPrefix, weight: null, timeframe: null };
};

/**
 * The shared "Reason" label for any alert surface.
 *
 * `reason` is NOT guaranteed to be one of the `AlertReason` enum codes. The
 * declarative task-rules engine (KAN-1859 / KAN-2238) stamps the rule's own
 * human-readable *name* into this field — e.g. "No coaching call scheduled"
 * arrives as the literal string "No calls scheduled" rather than
 * `NO_CALLS_SCHEDULED` — and more rules move onto that engine over time.
 *
 * So the translation lookup must degrade to the raw value when the key is
 * missing, exactly like `getAlertDetails` above and the table's
 * TRANSLATE_PREFIX cell. Without the fallback i18n echoes the key back and the
 * UI renders "ENUMS.ALERT_REASON.No calls scheduled" (KAN-2384).
 */
export const getAlertReason = (t: TFunction, reason: string | null | undefined): string => {
	if (!reason) return "";
	const prefix = "ENUMS.ALERT_REASON.";
	const translatedValue = t(prefix + reason);
	return translatedValue.startsWith(prefix) ? reason : translatedValue;
};

/**
 * Reason filter options for the alerts table. Built from the enum VALUES (what
 * the stored reason and the translation keys use), not the enum keys: KAN-2647
 * added CONSENT = "Consent", whose key differs from its value.
 */
export const getAlertReasonFilterOptions = (): { id: string; label: string }[] =>
	Object.values(AlertReason).map((r) => ({ id: r, label: "ENUMS.ALERT_REASON." + r }));

export const getAlertDetails = (t: TFunction, reason: AlertReason, details: string): string => {
	if (!details) return "";
	const prefix = "ENUMS.ALERT_DETAILS.";
	if (reason === AlertReason.SURVEY_COMPLETED) {
		return details;
	}
	if (details.startsWith("SURVEY_COMPLETED")) {
		return t(prefix + "SURVEY_COMPLETED");
	}
	if (details.startsWith("ABNORMAL_WEIGHT_GAIN")) {
		return t(prefix + "ABNORMAL_WEIGHT_GAIN");
	}
	if (details.startsWith(AlertDetails.NO_READINGS)) {
		return t(prefix + AlertDetails.NO_READINGS, {
			days: Number(details.replace(AlertDetails.NO_READINGS, "")),
		});
	}
	const translatedValue = t(prefix + details);
	return translatedValue.startsWith(prefix) ? details : translatedValue;
};

/**
 * The shared "Details" cell for any alert table (the population-level Requires
 * attention table and the participant-profile Requires attention tab render the
 * exact same cell). Kept here so the two views can never drift apart.
 */
/**
 * The shared "Reason" cell for any alert table. The population-level Requires
 * attention table and the participant-profile Requires attention tab both use
 * this, so the tables, the quick-view card and the edit modal all resolve the
 * label through `getAlertReason` — one implementation, four surfaces (KAN-2384).
 */
export const renderAlertReasonCell = (t: TFunction, rowData: TaskRow) => (
	<>{getAlertReason(t, rowData.reason as string)}</>
);

export const renderAlertDetailsCell = (t: TFunction, rowData: TaskRow) => {
	const weightGainData = parseAbnormalWeightGain(rowData.details as string);
	const outOfRange = parseAlertMetadata(rowData.metadata);

	return (
		<div>
			<div>
				{getAlertDetails(t, rowData.reason as AlertReason, rowData.details as string)}
			</div>
			{weightGainData?.weight && (
				<div className="text-sm text-muted">
					Weight: {weightGainData.weight} lbs, Timeframe: {weightGainData.timeframe}
				</div>
			)}
			{outOfRange?.map((entry) => (
				<div
					key={entry.measurement}
					className="text-sm text-muted"
				>
					{formatOutOfRangeAverage(entry)}
				</div>
			))}
		</div>
	);
};
